/**
 * usePlannedTasks Hook
 *
 * 獲取和排序有 plan 日期的任務，用於 Planned Layer 顯示。
 *
 * 排序優先級：
 * 1. 當天計劃 (plan === today)
 * 2. 當週計劃 (plan 在本週範圍內)
 * 3. 已過期 (plan < today)
 * 4. 當月計劃 (plan 在本月範圍內)
 * 5. 其他 (按 createdAt DESC)
 */
import { useMemo } from 'react';
import { format } from 'date-fns';
import { Task } from '../types/task';
import { useTasks } from './useTasks';
import { getCurrentUserId } from '../services/auth';

// 任務優先級類型（6 = 未設定 plan date）
type PlanPriority = 1 | 2 | 3 | 4 | 5 | 6;

// 分組任務介面
export interface PlannedTaskGroups {
  today: Task[];      // 當天計劃
  thisWeek: Task[];   // 當週計劃（不含今天）
  overdue: Task[];    // 已過期
  thisMonth: Task[];  // 當月計劃（不含本週）
  later: Task[];      // 更晚（有 plan date 但在本月之後）
  other: Task[];      // 未設定 plan date（等待用戶安排）
}

export interface UsePlannedTasksReturn {
  // 分組任務
  groups: PlannedTaskGroups;

  // 合併並排序的列表
  sortedTasks: Task[];

  // 統計
  totalCount: number;
  todayCount: number;
  overdueCount: number;

  // 狀態
  isLoading: boolean;
  isEmpty: boolean;
  error: Error | null;
}

/**
 * 獲取日期的開始時間 (00:00:00)
 */
function getStartOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * 獲取本週的開始日期 (週一)
 */
function getStartOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // 調整到週一
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * 獲取本週的結束日期 (週日 23:59:59)
 */
function getEndOfWeek(date: Date): Date {
  const startOfWeek = getStartOfWeek(date);
  const d = new Date(startOfWeek);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * 獲取本月的開始日期
 */
function getStartOfMonth(date: Date): Date {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * 獲取本月的結束日期
 */
function getEndOfMonth(date: Date): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * 將 YYYY-MM-DD 字串轉為 Date（設為當天開始）
 */
function parsePlanDate(planString: string): Date | null {
  if (!planString) return null;

  const parts = planString.split('-');
  if (parts.length !== 3) return null;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // 月份從 0 開始
  const day = parseInt(parts[2], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  return new Date(year, month, day, 0, 0, 0, 0);
}

/**
 * 計算任務的優先級
 */
function calculatePriority(task: Task, now: Date): PlanPriority {
  // 未設定 plan date 的任務，優先級最低
  if (!task.plan || task.plan.trim() === '') {
    return 6;
  }

  const planDate = parsePlanDate(task.plan);
  if (!planDate) return 6;

  const today = getStartOfDay(now);
  const endOfWeek = getEndOfWeek(now);
  const endOfMonth = getEndOfMonth(now);

  // 當天計劃
  if (planDate.getTime() === today.getTime()) {
    return 1;
  }

  // 當週計劃（今天之後，本週內）
  if (planDate > today && planDate <= endOfWeek) {
    return 2;
  }

  // 已過期（早於今天）
  if (planDate < today) {
    return 3;
  }

  // 當月計劃（本週之後，本月內）
  if (planDate > endOfWeek && planDate <= endOfMonth) {
    return 4;
  }

  // 更晚（下個月或更遠，有 plan date）
  return 5;
}

/**
 * 對任務進行分組
 */
function groupTasks(tasks: Task[], now: Date): PlannedTaskGroups {
  const groups: PlannedTaskGroups = {
    today: [],
    thisWeek: [],
    overdue: [],
    thisMonth: [],
    later: [],
    other: [],
  };

  for (const task of tasks) {
    const priority = calculatePriority(task, now);

    switch (priority) {
      case 1:
        groups.today.push(task);
        break;
      case 2:
        groups.thisWeek.push(task);
        break;
      case 3:
        groups.overdue.push(task);
        break;
      case 4:
        groups.thisMonth.push(task);
        break;
      case 5:
        groups.later.push(task);
        break;
      case 6:
        groups.other.push(task);
        break;
    }
  }

  return groups;
}

/**
 * 排序任務列表
 */
function sortTasks(tasks: Task[], now: Date): Task[] {
  return [...tasks].sort((a, b) => {
    // 1. 先按優先級排序
    const priorityA = calculatePriority(a, now);
    const priorityB = calculatePriority(b, now);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // 2. 同優先級內，按 plan 日期排序
    const planDateA = a.plan ? parsePlanDate(a.plan) : null;
    const planDateB = b.plan ? parsePlanDate(b.plan) : null;

    if (planDateA && planDateB) {
      const timeDiff = planDateA.getTime() - planDateB.getTime();
      if (timeDiff !== 0) return timeDiff;
    }

    // 3. plan 日期相同，按創建時間倒序
    return b.createdAt.getTime() - a.createdAt.getTime();
  });
}

/**
 * usePlannedTasks Hook
 *
 * 使用現有的 useTasks hook 數據，過濾出有 plan 的開放任務並排序
 */
export function usePlannedTasks(): UsePlannedTasksReturn {
  const userId = getCurrentUserId();
  const { tasks, isLoading, error } = useTasks(userId);

  // 當前時間（用於計算優先級）
  const now = useMemo(() => new Date(), []);

  // 過濾狀態為 OPEN 且未被排程（或排程已過期）的任務
  const plannedTasks = useMemo(() => {
    const today = format(now, 'yyyy-MM-dd');
    return tasks.filter((task) => {
      if (task.status !== 'OPEN') return false;
      // lastTimeboxedDate 不存在 → 未排程，顯示
      // today > lastTimeboxedDate → 排程日已過，重新浮現
      // today <= lastTimeboxedDate → 已安排未到期，隱藏
      if (task.lastTimeboxedDate && today <= task.lastTimeboxedDate) return false;
      return true;
    });
  }, [tasks, now]);

  // 分組任務
  const groups = useMemo(() => {
    return groupTasks(plannedTasks, now);
  }, [plannedTasks, now]);

  // 排序任務
  const sortedTasks = useMemo(() => {
    return sortTasks(plannedTasks, now);
  }, [plannedTasks, now]);

  // 統計
  const totalCount = plannedTasks.length;
  const todayCount = groups.today.length;
  const overdueCount = groups.overdue.length;
  const isEmpty = totalCount === 0;

  return {
    groups,
    sortedTasks,
    totalCount,
    todayCount,
    overdueCount,
    isLoading,
    isEmpty,
    error,
  };
}

export default usePlannedTasks;
